import {writable, get, derived} from "svelte/store";
import { round, type XSSDetails } from "@/Util/utils";
import { format, differenceInCalendarDays, addWeeks }from "date-fns";
import {ProgramType, max_tl_week} from "@/Common/constants";

export type AchievabilityStatus = "warning" | "error" | "fine";

export interface TargetAchievability {
    low: AchievabilityStatus,
    high: AchievabilityStatus,
    peak: AchievabilityStatus,
}

export interface RaceAchievability {
    mpa: RaceAchievabilityProperty,
    carb_rate: CarbRateAchievability,
    difficulty: DifficultyAchievability,
    event_readiness: EventReadinessAchievability,
    readyBy: "now" | Date | "never",
}

interface RaceAchievabilityProperty {
    status: AchievabilityStatus
}

interface EventReadinessAchievability extends RaceAchievabilityProperty {
    low_er_achievable: number,
    high_er_achievable: number,
    peak_er_achievable: number,
}

interface CarbRateAchievability extends RaceAchievabilityProperty {
    carb_rate_achievable: number,
}

interface DifficultyAchievability extends RaceAchievabilityProperty {
    difficulty_achievable: number,
}

export interface HoursAchievability extends RaceAchievabilityProperty {
    hours_achievable: number
}

export function createProgramAchievability(program: ProgramType) {
    const targetAchievability = writable<TargetAchievability>(createTargetAchievability("fine"))
    const raceAchievability = writable<RaceAchievability>(createRaceAchievability("fine"))
    const hoursAchievability = writable<HoursAchievability>(createHoursAchievability("fine"))
    const overallAchievability = derived(
        [targetAchievability, raceAchievability, hoursAchievability], 
        () => getOverallStatus(),
    )
    const statusMessage = writable<string>("You will likely reach your targets")
    const programType = program
    // const primaryTarget = programType === ProgramType.Goal ? "fitness signature" : "training load"

    const xssPerHour = 60
    const xlssTau = 60

    function createTargetAchievability(status: AchievabilityStatus): TargetAchievability {
        return {
            low: status,
            high: status,
            peak: status,
        }   
    }

    function createHoursAchievability(status: AchievabilityStatus): HoursAchievability {
        return {
            status: status,
            hours_achievable: 28,
        }
    }

    function createRaceAchievability(status: AchievabilityStatus): RaceAchievability {
        return {
            mpa: {
                status: status
            },
            carb_rate: {
                status: status,
                carb_rate_achievable: 0,
            },
            difficulty: {
                status: status,
                difficulty_achievable: 0,
            },
            event_readiness: {
                status: status,
                low_er_achievable: 0,
                high_er_achievable: 0,
                peak_er_achievable: 0,
            },
            readyBy: "now"
        }
    }

    function setStatus(status: AchievabilityStatus) {
        targetAchievability.set(createTargetAchievability(status))
        raceAchievability.set(createRaceAchievability(status))
        hoursAchievability.set(createHoursAchievability(status))
    }

    function getOverallStatus(): AchievabilityStatus {
        const raceValues = Object.values(get(raceAchievability)).flatMap(obj => Object.values(obj))
        const values = Object.values({
            ...get(targetAchievability),
            ...raceValues,
            hours: get(hoursAchievability).status,
        })

        if (values.some(status => status === 'error')) {
            return 'error';
        } else if (values.some(status => status === 'warning')) {
            return 'warning';
        } else {
            return 'fine';
        }
    }

    function getWeeklyHours(weeks: number, targetTL: XSSDetails, currentTL: XSSDetails) {
        const dailyRampRate = (targetTL?.xss - currentTL.xss)/weeks/7;
        return (targetTL?.xss*(1-(Math.exp(-1/xlssTau)))+dailyRampRate)/(1-Math.exp(-1/xlssTau)) * 7 / xssPerHour
    }

    function validateWeeklyHours(endDate: Date, startDate: Date, targetTL: XSSDetails, currentTL: XSSDetails, maxWeeklyHours: number) {
        const weeks = differenceInCalendarDays(endDate, startDate)/7.0
        // Compare unrounded: rounding first hid small shortfalls, e.g. 5.53h needed vs 5.5h available
        const weeklyHoursNeeded = getWeeklyHours(weeks, targetTL, currentTL)
        hoursAchievability.set({
            status: weeklyHoursNeeded > 28 ? "error" : weeklyHoursNeeded > maxWeeklyHours ? "warning" : "fine",
            hours_achievable: round(weeklyHoursNeeded, 1),
        })
        updateStatusMessage(endDate)
    }

    function updateStatusMessage(endDate:Date) {
        // Create status message 
        let formattedDate = format(endDate, "LLLL dd, yyyy")
        let message: string
        switch(get(overallAchievability)) {
            case "error":
                message = get(hoursAchievability).status === "error" ? 
                    "You may not have sufficient weekly hours available. Forecast AI may not be able to generate a suitable plan." : 
                    "Your goal is outside the typical range of achievability. Forecast AI may not be able to generate a suitable plan."
                break
            case "warning":
                message = get(hoursAchievability).status === "warning" ? 
                    "You may not have sufficient weekly hours available. However, Forecast AI may still be able to generate a suitable plan." : 
                    "Your goal is outside the typical range of achievability. However, Forecast AI may still be able to generate a suitable plan."
                break
            case "fine":
                message = "Your goal is within the typical range of achievability. Forecast AI will likely be able to generate a suitable plan."
                break
        }

        statusMessage.set(message)
    }

    /* Validation for Event / Goal */
    function validateTargets(endDate: Date, startDate: Date, targetTL: XSSDetails, currentTL: XSSDetails, maxWeeklyHours: number, difficulty?: number) {

        const weeks = differenceInCalendarDays(endDate, startDate)/7.0

        // Reset 
        setStatus("fine")

        let maxTL = {...currentTL};
        if (!difficulty) {
            for (let i = 0; i < weeks*7; ++i) {
                let scale = Math.max(50, Math.min(150, maxTL.xss)) / 100.0;
                let low_multiplier = Math.pow(scale, 0.8);
                let high_multiplier = 0.173*Math.exp(1.89*scale);
                maxTL.xlss += 1.0/7*max_tl_week.low * low_multiplier;
                maxTL.xhss += 1.0/7*max_tl_week.high * high_multiplier;
                maxTL.xpss += 1.0/7*max_tl_week.peak * high_multiplier;
                maxTL.xss = maxTL.xlss + maxTL.xhss + maxTL.xpss;
            }
        } else {
            let scale = difficulty/100.0;
            let low_multiplier = Math.pow(scale, 0.8);
            let high_multiplier = 0.173*Math.exp(1.89*scale);
            maxTL.xlss += weeks*max_tl_week.low * low_multiplier;
            maxTL.xhss += weeks*max_tl_week.high * high_multiplier;
            maxTL.xpss += weeks*max_tl_week.peak * high_multiplier;
            maxTL.xss = maxTL.xlss + maxTL.xhss + maxTL.xpss;
        }

        // Set Achievability
        let achievability: TargetAchievability = createTargetAchievability("fine")

        achievability.low = maxTL.xlss < targetTL.xlss 
            ? "error" 
            : (weeks*max_tl_week.low + currentTL.xlss < targetTL.xlss ? "warning" : "fine")
        
        achievability.high = maxTL.xhss < targetTL.xhss 
            ? "error" 
            : (weeks*max_tl_week.high + currentTL.xhss < targetTL.xhss ? "warning" : "fine") 
    
        achievability.peak = maxTL.xpss < targetTL.xpss 
            ? "error" 
            : (weeks*max_tl_week.peak + currentTL.xpss < targetTL.xpss ? "warning" : "fine")

        targetAchievability.set(achievability)
        validateWeeklyHours(endDate, startDate, targetTL, currentTL, maxWeeklyHours) 
        
        updateStatusMessage(endDate)
    
        return get(overallAchievability) === "fine"
    }

    /* Validation for a Goal against its plan's training capacity instead of fixed ramp rates: a Focus Goal against its
       estimated frontier (assessGoal), where the systems the plan raises carry the goal's status, or manual signature
       targets (assessManualGoal), where each system has its own. The message goes with them unless the weekly hours fall
       short. */
    function validateFocusGoal(endDate: Date, startDate: Date, targetTL: XSSDetails, currentTL: XSSDetails, maxWeeklyHours: number,
                               status: AchievabilityStatus | TargetAchievability, message: string) {
        setStatus("fine")
        const of = (system: keyof TargetAchievability) => typeof status === "string" ? status : status[system]
        targetAchievability.set({
            low: targetTL.xlss > currentTL.xlss ? of("low") : "fine",
            high: targetTL.xhss > currentTL.xhss ? of("high") : "fine",
            peak: targetTL.xpss > currentTL.xpss ? of("peak") : "fine",
        })
        validateWeeklyHours(endDate, startDate, targetTL, currentTL, maxWeeklyHours)
        const fine = Object.values(get(targetAchievability)).every(s => s === "fine")
        if (!fine || get(hoursAchievability).status === "fine") statusMessage.set(message)
        return get(overallAchievability) === "fine"
    }

    /* Validation for Race */
    function validateRace(raceStepResults, endDate: Date, startDate: Date, targetTL: XSSDetails, currentTL: XSSDetails, maxWeeklyHours: number) {

        // Reset the page achievability
        setStatus("fine")
        let valid = true

        let achievability: RaceAchievability = {
            ...get(raceAchievability),
            mpa: {
                status: raceStepResults['mpa'] ? (raceStepResults['sensitivity'] ? "fine" : "warning") : "error",
            },
            carb_rate: {
                status: raceStepResults['carb_rate'] ? "fine" : "error",
                carb_rate_achievable: raceStepResults["carb_rate_achievable"],
            },
            difficulty: {
                status: raceStepResults['difficulty'] ? "fine" : "error",
                difficulty_achievable: raceStepResults["difficulty_achievable"],
            },
            event_readiness: {
                status: raceStepResults['low_er'] && raceStepResults['high_er'] && raceStepResults['peak_er'] ? "fine" : "error",
                low_er_achievable: raceStepResults["low_er_achievable"],
                high_er_achievable: raceStepResults["high_er_achievable"],
                peak_er_achievable: raceStepResults["peak_er_achievable"],
            },
        }
        
        validateWeeklyHours(endDate, startDate, targetTL, currentTL, maxWeeklyHours) 

        if (raceStepResults["weeks_until_ready"]) {
            valid = false
            achievability.readyBy = raceStepResults["weeks_until_ready"]*7 < 365 ? addWeeks(new Date(), Math.ceil(raceStepResults["weeks_until_ready"])) : "never"
        }

        raceAchievability.set(achievability)

        return valid
    }

    /* The race's ready date from elsewhere: the capacity simulator, in the race form */
    function setReadyBy(readyBy: RaceAchievability["readyBy"]) {
        raceAchievability.update(achievability => ({...achievability, readyBy}))
    }

    /* Validation for Xata Programs */
    function validateRampRates(trainingGradient: number, currentTL: XSSDetails) {
        setStatus("fine")

        // Set Achievability
        let achievability: TargetAchievability = createTargetAchievability("fine")

        achievability.low = (currentTL.xlss/currentTL.xss) * trainingGradient >= max_tl_week.low ? "warning" : "fine"
        achievability.high = (currentTL.xhss/currentTL.xss) * trainingGradient >= max_tl_week.high ? "warning" : "fine"
        achievability.peak = (currentTL.xpss/currentTL.xss) * trainingGradient >= max_tl_week.peak ? "warning" : "fine"

        targetAchievability.set(achievability)

        switch(get(overallAchievability)) {
            case "warning":
                statusMessage.set("Your improvement rate might be unsustainable. You may have to switch to a slower improvement rate later") 
                break
            case "fine":
                statusMessage.set("Your improvement rate is likely to be sustainable.")
                break
        }
    }

    function getRampRates() {
        return {low: max_tl_week.low, high: max_tl_week.high, peak: max_tl_week.peak}
    }

    return {
        targetAchievability,
        raceAchievability,
        hoursAchievability,
        overallAchievability,
        statusMessage,
        setStatus,
        validateTargets,
        validateFocusGoal,
        validateRace,
        setReadyBy,
        validateWeeklyHours,
        validateRampRates,
        getRampRates,
    }
}

export type ProgramAchievability = ReturnType<typeof createProgramAchievability>